import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { Picker } from 'emoji-picker-element';
import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';

export const interactionsMixin = {

    //********************* OPEN FANCYBOX ********************************//
    // Opens Fancybox at the clicked message, with all images and PDFs in the
    // active conversation as slides so the user can swipe through them.
    // * messageId (string) — message_id of the item that was clicked
    openFancybox(messageId) {
        const media = this.activeMessages().filter(m =>
            m.mime_type?.startsWith('image/') || m.mime_type === 'application/pdf'
        );

        const startIndex = media.findIndex(m => m.message_id === messageId);
        if(!media.length || startIndex === -1) return;

        const items = media.map(m => ({
            src: m.content,
            type: m.mime_type === 'application/pdf' ? 'iframe' : 'image',
            caption: m.file_name || '',
            thumbSrc: m.thumbnail_url || (m.mime_type?.startsWith('image/') ? m.content : null)
        }));

        Fancybox.show(items, {
            startIndex,
            Toolbar: {
                display: {
                    left: ['infobar'],
                    middle: [],
                    right: ['iterateZoom', 'slideshow', 'fullscreen', 'thumbs', 'close']
                }
            }
        });
    },

    //********************* WIRE MESSAGE ACTIONS ********************************//
    // Wires up a rendered message's hover toolbar ("Add reaction" / "Reply in
    // thread") and its reaction pills (see renderMessageHtml/
    // renderReactionsHtml). Called for every message added to the chat, and
    // for the thread_parent_message slot in renderActiveConversation.
    // * messageEl (HTMLElement) — the `.message` element (see renderMessageHtml)
    // * message (Object) — normalized message (see normalizeMessage)
    wireMessageActions(messageEl, message) {
        const reactButton = messageEl.querySelector('.message_toolbar__react');
        if(reactButton) reactButton.onclick = (event) => this.showReactionPicker(event, message.message_id);

        const replyButton = messageEl.querySelector('.message_toolbar__reply');
        if(replyButton) replyButton.onclick = () => this.openMessageThread(message.message_id);

        const threadFooter = messageEl.querySelector('.message__thread_footer');
        if(threadFooter) threadFooter.onclick = () => this.openMessageThread(message.message_id);

        const deleteBtn = messageEl.querySelector('.file_card__delete');
        if(deleteBtn) deleteBtn.onclick = () => this.deleteFile(message.message_id, message.filename);

        for(const link of messageEl.querySelectorAll('a[download]')) {
            link.onclick = (e) => {
                e.preventDefault();
                this.downloadFile(link.href, link.getAttribute('download'));
            };
        }

        if(message.mime_type?.startsWith('image/')) {
            const imgEl = messageEl.querySelector('.file_image__img');
            if(imgEl) {
                imgEl.style.cursor = 'pointer';
                imgEl.onclick = (e) => { e.preventDefault(); this.openFancybox(message.message_id); };
            }
        } else if(message.mime_type === 'application/pdf') {
            for(const el of messageEl.querySelectorAll('.file_card__preview, .file_card__thumbnail')) {
                el.style.cursor = 'pointer';
                el.onclick = () => this.openFancybox(message.message_id);
            }
        }

        this.wireReactionPills(messageEl, message.message_id);
    },

    //********************* DOWNLOAD FILE ********************************//
    // Forces a save-as download instead of navigating the current tab, which
    // is what happens when the <a download> target is cross-origin (the
    // browser ignores the `download` attribute in that case). Fetches the
    // file as a Blob and downloads it via a same-origin blob: URL instead.
    // * url (string) — file URL (message.content)
    // * filename (string) — value for the anchor's download attribute
    async downloadFile(url, filename) {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename || '';
            document.body.append(a);
            a.click();
            a.remove();

            URL.revokeObjectURL(objectUrl);
        } catch {
            window.open(url, '_blank');
        }
    },

    //********************* WIRE REACTION PILLS ********************************//
    // Wires up each reaction pill rendered by renderReactionsHtml() to toggle
    // the active user's reaction for that emoji.
    // * messageEl (HTMLElement) — the `.message` element (see renderMessageHtml)
    // * messageId (string) — message_id of the message being reacted to
    wireReactionPills(messageEl, messageId) {
        messageEl.querySelectorAll('.message_reaction').forEach(pill => {
            pill.onclick = () => this.toggleReaction(messageId, pill.dataset.emoji);
        });
    },

    //********************* SHOW REACTION PICKER ********************************//
    // "Add reaction" toolbar button - opens an <emoji-picker> popover anchored
    // to the message's toolbar (lazy-created on first use, same pattern as
    // gh-input's emoji button). Selecting an emoji toggles a reaction via
    // toggleReaction() and closes the popover; clicking outside also closes it.
    // * event (MouseEvent) — click on .message_toolbar__react
    // * messageId (string) — message_id to react to
    showReactionPicker(event, messageId) {
        event.preventDefault();
        event.stopPropagation();

        const toolbar = event.currentTarget.closest('.message_toolbar');
        let popover = toolbar.querySelector('.emoji-picker-popover');

        if(!popover) {
            popover = document.createElement('div');
            popover.className = 'emoji-picker-popover';

            const picker = new Picker();
            popover.append(picker);
            toolbar.append(popover);

            picker.addEventListener('emoji-click', e => {
                this.toggleReaction(messageId, e.detail.unicode);
                popover.classList.remove('active');
            });
        }

        popover.classList.toggle('active');

        if(popover.classList.contains('active')) {
            this.positionReactionPicker(toolbar, popover);

            const listener = (e) => {
                if(!popover.contains(e.target) && e.target !== event.currentTarget) {
                    popover.classList.remove('active');
                    window.removeEventListener('click', listener);
                }
            };

            window.addEventListener('click', listener);
        }
    },

    //********************* POSITION REACTION PICKER ********************************//
    // Places the emoji-picker popover (position: fixed) below (or above, if
    // there isn't room) the toolbar button that opened it, centered
    // horizontally over the .chat column - keeps the picker from being
    // clipped by the chat's scroll container (see showReactionPicker).
    // * toolbar (HTMLElement) — .message_toolbar the popover is appended to
    // * popover (HTMLElement) — .emoji-picker-popover (already made visible)
    positionReactionPicker(toolbar, popover) {
        const toolbarRect = toolbar.getBoundingClientRect();
        const chatRect = this.querySelector('.chat').getBoundingClientRect();
        const popoverWidth = popover.offsetWidth;
        const popoverHeight = popover.offsetHeight;

        const openUpward = toolbarRect.bottom + popoverHeight > window.innerHeight;

        popover.style.top = openUpward
            ? `${toolbarRect.top - popoverHeight - 4}px`
            : `${toolbarRect.bottom + 4}px`;

        const centeredLeft = chatRect.left + (chatRect.width - popoverWidth) / 2;
        popover.style.left = `${Math.max(8, Math.min(centeredLeft, window.innerWidth - popoverWidth - 8))}px`;
    },

    //********************* TOGGLE REACTION ********************************//
    // Adds/removes the active user's reaction with `emoji` on a message via
    // POST .../message/:message_id/reaction, then updates the local message
    // and its rendered reaction pills (see updateMessageReactions).
    // * messageId (string) — message_id to react to
    // * emoji (string) — unicode emoji character
    async toggleReaction(messageId, emoji) {
        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${this.conversationId}/message/${messageId}/reaction`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: this.activeUserId, emoji })
        });

        const { reactions } = await response.json();

        const message = this.findMessageById(messageId);
        if(message) message.reactions = reactions;

        this.updateMessageReactions(messageId, reactions);
    },

    //********************* FIND MESSAGE BY ID ********************************//
    // Looks up a message by id among the active view's messages, falling back
    // to the thread_parent_message (not part of activeMessages() - see
    // renderActiveConversation).
    // * messageId (string) — message_id to find
    findMessageById(messageId) {
        return this.activeMessages().find(message => message.message_id === messageId)
            || (this.threadState?.parentMessage?.message_id === messageId ? this.threadState.parentMessage : null);
    },

    //********************* UPDATE MESSAGE REACTIONS ********************************//
    // Re-renders a message's reaction pills in place after toggleReaction() or
    // a live reaction_added/reaction_removed push (see handleEmitToUser).
    // * messageId (string) — message_id whose pills should be updated
    // * reactions (Array) — updated reactions array (see normalizeMessage)
    updateMessageReactions(messageId, reactions) {
        const messageEl = this.querySelector(`.message[data-message-id="${messageId}"]`);
        if(!messageEl) return;

        const existing = messageEl.querySelector('.message_reactions');
        const html = this.renderReactionsHtml({ reactions });

        if(existing) existing.remove();
        if(html) messageEl.querySelector('.message').insertAdjacentHTML('afterend', html);

        this.wireReactionPills(messageEl, messageId);
    },
};
